// mock-data.js — deterministic in-memory dataset for ?mock=1 (or VITE_OPS_MOCK).
// Lets every screen render fully without a backend. Produces ~45 days of rollups
// across providers × hospitals (demo/production/platform) × departments × doctors ×
// use-cases, plus prices, telemetry (with confidence/steps/latency), manual costs
// and one fx-rate. Shapes match exactly what the live fetch* functions return, so
// screens are agnostic to the source.
//
// Determinism: a seeded PRNG (mulberry32) → the same dataset every load, so demos
// and screenshots are stable.
import { DEFAULT_PRICES, SEED_EFFECTIVE_FROM } from './default-prices.js';
import { shiftDay, todayIso } from './format.js';

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DAYS = 45;

// Display-name mapping for doctor ids (contract §1: the dashboard resolves names
// from a small manual mapping or shows the id).
export const DOCTOR_NAMES = {
  'doc-arun-mehta': 'Dr Arun Mehta',
  'doc-meera-nair': 'Dr Meera Nair',
  'doc-asha-verma': 'Dr Asha Verma',
  'doc-vikram-rao': 'Dr Vikram Rao',
  'doc-priya-shah': 'Dr Priya Shah',
  'doc-sanjay-iyer': 'Dr Sanjay Iyer',
};
const DOCTORS = Object.keys(DOCTOR_NAMES);
const DEPARTMENTS = ['cardiology', 'general-medicine', 'orthopedics'];

// A "meter": one provider/product/metric emitted for a use-case, with typical
// attribution and a per-hospital daily base quantity. `attributed` meters get a
// department + doctor; platform/front-desk ones use '-'.
const METERS = [
  { provider: 'openai', product: 'gpt-5-4-mini', metric: 'input-tokens', usecase: 'consult-draft', attributed: true, base: 42000 },
  { provider: 'openai', product: 'gpt-5-4-mini', metric: 'output-tokens', usecase: 'consult-draft', attributed: true, base: 9000 },
  { provider: 'openai', product: 'gpt-5-4-mini', metric: 'input-tokens', usecase: 'conflict-review', attributed: true, base: 12000 },
  { provider: 'openai', product: 'gpt-5-4-mini', metric: 'output-tokens', usecase: 'conflict-review', attributed: true, base: 2600 },
  { provider: 'cerebras', product: 'gemma-4-31b', metric: 'input-tokens', usecase: 'live-intake', attributed: true, base: 88000 },
  { provider: 'cerebras', product: 'gemma-4-31b', metric: 'output-tokens', usecase: 'live-intake', attributed: true, base: 14000 },
  { provider: 'deepgram', product: 'nova-3', metric: 'audio-seconds', usecase: 'live-intake', attributed: true, base: 5400 },
  { provider: 'openai', product: 'gpt-4o-mini', metric: 'input-tokens', usecase: 'voice-extract', attributed: true, base: 26000 },
  { provider: 'openai', product: 'gpt-4o-mini', metric: 'output-tokens', usecase: 'voice-extract', attributed: true, base: 5200 },
  { provider: 'sarvam', product: 'saaras', metric: 'audio-seconds', usecase: 'kiosk-intake', attributed: false, base: 7200 },
  { provider: 'sarvam', product: 'bulbul', metric: 'characters', usecase: 'kiosk-intake', attributed: false, base: 48000 },
  { provider: 'cerebras', product: 'gemma-4-31b', metric: 'input-tokens', usecase: 'kiosk-intake', attributed: false, base: 61000 },
  { provider: 'gemini', product: 'gemini-3-1-flash-live', metric: 'input-tokens', usecase: 'ipd-verifier', attributed: true, base: 31000 },
  { provider: 'gemini', product: 'gemini-3-1-flash-live', metric: 'output-tokens', usecase: 'ipd-verifier', attributed: true, base: 6400 },
  { provider: 'openai-realtime', product: 'gpt-realtime', metric: 'audio-seconds', usecase: 'realtime', attributed: false, base: 2600 },
  { provider: 'elevenlabs', product: 'convai', metric: 'audio-seconds', usecase: 'realtime', attributed: false, base: 3100 },
  { provider: 'gupshup', product: 'wa-message', metric: 'messages', usecase: 'messaging', attributed: false, base: 340 },
];

// Providers that also report a provider-billed total (source=provider-api) so the
// reconciliation chip has metered-vs-billed to compare. Rough per-day USD amounts.
const PROVIDER_BILLED = [
  { provider: 'openai', product: '-', usd: 6.2 },
  { provider: 'deepgram', product: '-', usd: 1.1 },
  { provider: 'gemini', product: '-', usd: 2.4 },
  { provider: 'elevenlabs', product: '-', usd: 4.8 },
];

// Weekend/weekday + slow upward trend shape for a given day index (0 = oldest).
function dayFactor(rng, i, dateIso) {
  const dow = new Date(dateIso + 'T00:00:00Z').getUTCDay();
  const weekend = (dow === 0 || dow === 6) ? 0.45 : 1;
  const trend = 0.7 + (i / DAYS) * 0.6; // ramps up ~1.3x over the window
  const noise = 0.82 + rng() * 0.36;
  return weekend * trend * noise;
}

function buildRollups(rng, days) {
  const rows = [];
  const push = (day, source, hospital, provider, product, metric, usecase, department, doctor, value, unit, status) => {
    rows.push({
      id: `mr-${rows.length}`,
      day, source, hospital, provider, product, metric,
      usecase: usecase || '-', department: department || '-', doctor: doctor || '-',
      value, unit, status,
    });
  };

  days.forEach((day, i) => {
    const isToday = i === days.length - 1;
    const status = isToday ? 'pending' : 'complete';
    for (const hospital of ['demo', 'production']) {
      const hf = hospital === 'production' ? 1.9 : 1;
      for (const m of METERS) {
        const dept = m.attributed ? DEPARTMENTS[Math.floor(rng() * DEPARTMENTS.length)] : '-';
        const doctor = m.attributed ? DOCTORS[Math.floor(rng() * DOCTORS.length)] : '-';
        const qty = Math.round(m.base * hf * dayFactor(rng, i, day));
        if (qty <= 0) continue;
        push(day, 'metered', hospital, m.provider, m.product, m.metric, m.usecase, dept, doctor, qty, m.metric, status);
      }
    }
    // Provider-billed totals (platform-level, source=provider-api, billed-cost USD).
    for (const b of PROVIDER_BILLED) {
      const usd = +(b.usd * dayFactor(rng, i, day) * (0.95 + rng() * 0.2)).toFixed(4);
      push(day, 'provider-api', 'platform', b.provider, b.product, 'billed-cost', '-', '-', '-', usd, 'USD', status);
    }
    // AWS infra (platform, source=aws, billed-cost USD, unpriced-by-design).
    push(day, 'aws', 'platform', 'aws', 'ec2', 'billed-cost', '-', '-', '-',
      +(9.5 * (0.9 + rng() * 0.25)).toFixed(2), 'USD', status);
    push(day, 'aws', 'platform', 'aws', 's3', 'billed-cost', '-', '-', '-',
      +(1.6 * (0.9 + rng() * 0.3)).toFixed(2), 'USD', status);
  });
  return rows;
}

function buildPrices() {
  // Same seed the "Seed defaults" button would create, pre-materialized.
  return DEFAULT_PRICES.map((p, i) => ({
    id: `cid-${i}`,
    priceKey: p.priceKey,
    title: p.title,
    amount: p.amount,
    currency: p.currency,
    basis: p.basis,
    effectiveFrom: SEED_EFFECTIVE_FROM,
    unverified: true,
  }));
}

function buildTelemetry(rng, days) {
  const rows = [];
  const KINDS = [
    { kind: 'llm', provider: 'openai', product: 'gpt-5-4-mini', usecase: 'consult-draft', perDay: 14 },
    { kind: 'stt', provider: 'deepgram', product: 'nova-3', usecase: 'live-intake', perDay: 11 },
    { kind: 'stt', provider: 'sarvam', product: 'saaras', usecase: 'kiosk-intake', perDay: 8 },
    { kind: 'tts', provider: 'sarvam', product: 'bulbul', usecase: 'kiosk-intake', perDay: 8 },
    { kind: 'harness', provider: 'cerebras', product: 'gemma-4-31b', usecase: 'live-intake', perDay: 9 },
    { kind: 'agent-turn', provider: 'openai', product: 'gpt-5-4-mini', usecase: 'ipd-verifier', perDay: 6 },
    { kind: 'realtime', provider: 'gemini', product: 'gemini-3-1-flash-live', usecase: 'ipd-verifier', perDay: 4 },
  ];
  days.forEach((day, di) => {
    for (const k of KINDS) {
      const n = Math.max(1, Math.round(k.perDay * (0.6 + rng() * 0.8)));
      for (let j = 0; j < n; j++) {
        const hospital = rng() > 0.4 ? 'production' : 'demo';
        const attributed = k.usecase === 'consult-draft' || k.usecase === 'live-intake' || k.usecase === 'ipd-verifier';
        const dept = attributed ? DEPARTMENTS[Math.floor(rng() * DEPARTMENTS.length)] : '-';
        const doctor = attributed ? DOCTORS[Math.floor(rng() * DOCTORS.length)] : '-';
        // A late-window confidence dip on one day so AI-quality dip flags fire.
        const dip = di === days.length - 4 && k.kind === 'stt' ? 0.22 : 0;
        const conf = Math.min(0.995, Math.max(0.35, 0.9 - dip + (rng() - 0.5) * 0.18));
        const hh = String(8 + Math.floor(rng() * 11)).padStart(2, '0');
        const mm = String(Math.floor(rng() * 60)).padStart(2, '0');
        const ev = {
          id: `tel-${day}-${k.kind}-${j}`,
          kind: k.kind, hospital, provider: k.provider, product: k.product,
          usecase: k.usecase, department: dept, doctor,
          at: `${day}T${hh}:${mm}:00+05:30`,
          outcome: rng() > 0.94 ? (rng() > 0.5 ? 'error' : 'timeout') : 'ok',
          latencyMs: Math.round(700 + rng() * 4200 + (k.kind === 'realtime' ? 1500 : 0)),
        };
        if (k.kind === 'stt' || k.kind === 'realtime') { ev.confidence = +conf.toFixed(3); ev.audioSeconds = +(8 + rng() * 90).toFixed(1); }
        if (k.kind === 'tts') { ev.characters = Math.round(120 + rng() * 900); }
        if (k.kind === 'llm' || k.kind === 'agent-turn' || k.kind === 'harness' || k.kind === 'realtime') {
          ev.tokens = { input: Math.round(400 + rng() * 3000), output: Math.round(80 + rng() * 700) };
          if (k.kind === 'harness' || k.kind === 'agent-turn') { ev.steps = 1 + Math.floor(rng() * 7); ev.toolCalls = Math.floor(rng() * 10); }
          if (k.kind === 'llm' || k.kind === 'agent-turn') ev.confidence = +Math.min(0.99, 0.75 + rng() * 0.22).toFixed(3);
        }
        rows.push(ev);
      }
    }
  });
  return rows;
}

function buildManualCosts() {
  return [
    { id: 'mc-1', label: 'Apple Developer Program', category: 'store', amount: 99, currency: 'USD', cadence: 'yearly', startDate: '2026-01-15', endDate: null, notes: 'Kiosk + staff app distribution' },
    { id: 'mc-2', label: 'Google Play Console', category: 'store', amount: 25, currency: 'USD', cadence: 'one-time', startDate: '2026-01-10', endDate: null, notes: 'One-time registration' },
    { id: 'mc-3', label: 'AWS reserved instance (baseline)', category: 'infra', amount: 18000, currency: 'INR', cadence: 'monthly', startDate: '2026-03-01', endDate: null, notes: 'Mumbai EC2 committed use' },
    { id: 'mc-4', label: 'Sentry error monitoring', category: 'subscription', amount: 29, currency: 'USD', cadence: 'monthly', startDate: '2026-02-01', endDate: null, notes: 'Team plan' },
    { id: 'mc-5', label: 'Domain + TLS', category: 'infra', amount: 1400, currency: 'INR', cadence: 'yearly', startDate: '2026-01-01', endDate: null, notes: 'healther.tech' },
  ];
}

function buildFxRates() {
  return [
    { id: 'fx-1', base: 'USD', quote: 'INR', rate: 86.4, effectiveFrom: '2026-01-01' },
  ];
}

// A fresh, independently-seeded dataset (same seed → identical every time). Mutable
// arrays back the manual-cost / fx-rate / price CRUD so saves work in mock mode.
export function createMockDataset() {
  const rng = mulberry32(20260719);
  const today = todayIso();
  const days = [];
  for (let i = DAYS - 1; i >= 0; i--) days.push(shiftDay(today, -i));
  return {
    rollups: buildRollups(rng, days),
    prices: buildPrices(),
    telemetry: buildTelemetry(rng, days),
    manualCosts: buildManualCosts(),
    fxRates: buildFxRates(),
  };
}
